/**
 * Shared template for every step in the onboarding flow.
 *
 * Locks in a single visual shell so every page in the funnel feels like
 * one designed system. Each onboarding step (value pitch, intake question,
 * plan preview, etc.) renders inside this component and only provides
 * step-specific content as children.
 *
 * Layout (top to bottom):
 *   - AppHeaderBar (logo + lang + Help)
 *   - Progress row: back chevron · "Step N of M" · progress bar
 *   - Card with icon block + headline + subtitle + step content + CTA group
 *
 * Pass `totalSteps={0}` to hide the progress indicator (e.g. terminal
 * "you're done" screen).
 */
import React from 'react';
import { ActivityIndicator, ScrollView, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

import AppHeaderBar from '../layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { useResponsive } from '../../hooks/useResponsive';
import {
  ONBOARDING_COLORS as COLORS,
  ONBOARDING_CONTENT_MAX_WIDTH,
  ONBOARDING_CONTROL_HEIGHT,
  ONBOARDING_RADII,
  ONBOARDING_SPACING,
  ONBOARDING_STEP_CARD_MIN_HEIGHT,
  ONBOARDING_TYPOGRAPHY,
} from './onboardingTheme';

interface PrimaryCtaProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface SecondaryCtaProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}

interface Props {
  /** Current step index, 1-based. e.g. 2 renders "Step 2 of 5". */
  step: number;
  /** Total number of steps. Pass 0 to hide the progress row. */
  totalSteps: number;
  /** MaterialIcons glyph shown in the rounded-2xl primary block to the left of the title. */
  icon?: keyof typeof MaterialIcons.glyphMap;
  /** Bold headline. */
  title?: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Primary action — full-width primary button at the bottom of the card. */
  primaryCta: PrimaryCtaProps;
  /** Optional muted text-link shown below the primary button (e.g. "Skip"). */
  secondaryCta?: SecondaryCtaProps;
  /** Show a back chevron in the progress row. */
  onBack?: () => void;
  /** Override the right-side header slot (defaults to lang + Help). */
  headerRightSlot?: React.ReactNode;
  /**
   * Which part of the two-part setup this step belongs to (VIS-ONB-12). Shows
   * one progress line, "Part 1 of 2 · Tour · step 1 of 3", instead of two
   * competing totals.
   */
  journeyPart?: 'tour' | 'intake';
  /** Helper copy rendered inside the card footer below the actions. */
  helperText?: string;
  /** Step-specific content (radio cards, text input, plan summary, etc.). */
  children?: React.ReactNode;
}

export default function OnboardingStepLayout({
  step,
  totalSteps,
  icon,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  onBack,
  headerRightSlot,
  journeyPart,
  helperText,
  children,
}: Props) {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { isPhone } = useResponsive();
  const hasProgress = totalSteps > 0;
  const progressPct = hasProgress ? Math.min(1, Math.max(0, step / totalSteps)) : 0;
  const primaryDisabled = Boolean(primaryCta.disabled || primaryCta.loading);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: COLORS.surface }}>
      <AppHeaderBar rightSlot={headerRightSlot} />
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: ONBOARDING_SPACING.xl,
          paddingTop: ONBOARDING_SPACING.xxl,
          paddingBottom: Math.max(
            insets.bottom + ONBOARDING_SPACING.xxl,
            ONBOARDING_SPACING.xl * 2,
          ),
        }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
      >
        <View style={{ width: '100%', maxWidth: ONBOARDING_CONTENT_MAX_WIDTH, alignSelf: 'center' }}>
          {hasProgress ? (
            <View style={{ marginBottom: ONBOARDING_SPACING.xl }}>
              <View
                className="flex-row items-center justify-between"
                style={{ gap: ONBOARDING_SPACING.sm, marginBottom: ONBOARDING_SPACING.sm }}
              >
                <View style={{ flex: 1, minWidth: 0 }} className="flex-row items-center">
                  {onBack ? (
                    <TouchableOpacity
                      onPress={onBack}
                      accessibilityRole="button"
                      accessibilityLabel={t('intake.back')}
                      hitSlop={ONBOARDING_SPACING.sm}
                      className="flex-row items-center"
                      style={{
                        minHeight: ONBOARDING_CONTROL_HEIGHT,
                        minWidth: ONBOARDING_CONTROL_HEIGHT,
                        alignItems: 'center',
                      }}
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={ONBOARDING_SPACING.xl}
                        color={COLORS.text.secondary}
                      />
                      <Text
                        style={{
                          ...ONBOARDING_TYPOGRAPHY.bodySmall,
                          color: COLORS.text.secondary,
                          flexShrink: 1,
                        }}
                      >
                        {t('intake.back')}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                <Text
                  style={{
                    ...ONBOARDING_TYPOGRAPHY.caption,
                    color: COLORS.text.muted,
                    fontWeight: '500',
                    flexShrink: 0,
                  }}
                >
                  {journeyPart
                    ? t(`onboardingProgress.${journeyPart}`, { current: step, total: totalSteps })
                    : t('intake.stepProgress', { current: step, total: totalSteps })}
                </Text>
                <View style={{ flex: 1, minWidth: 0 }} />
              </View>
              <View
                className="overflow-hidden"
                style={{
                  height: ONBOARDING_SPACING.xs,
                  borderRadius: ONBOARDING_RADII.pill,
                  backgroundColor: COLORS.border,
                }}
              >
                <View
                  style={{
                    width: `${progressPct * 100}%`,
                    height: '100%',
                    backgroundColor: COLORS.primary,
                  }}
                />
              </View>
            </View>
          ) : null}

          <Card style={{ borderRadius: ONBOARDING_RADII.lg }}>
            <CardContent
              style={{
                gap: ONBOARDING_SPACING.xl,
                padding: isPhone ? ONBOARDING_SPACING.xl : ONBOARDING_SPACING.xxl,
                minHeight: isPhone ? undefined : ONBOARDING_STEP_CARD_MIN_HEIGHT,
              }}
            >
              {icon || title || subtitle ? (
                <View>
                  {icon || title ? (
                    <View
                      style={{
                        flexDirection: isPhone ? 'column' : 'row',
                        alignItems: isPhone ? 'flex-start' : 'center',
                        gap: ONBOARDING_SPACING.sm,
                        marginBottom: ONBOARDING_SPACING.sm,
                      }}
                    >
                      {icon ? (
                        <View
                          className="items-center justify-center"
                          style={{
                            width: ONBOARDING_CONTROL_HEIGHT,
                            height: ONBOARDING_CONTROL_HEIGHT,
                            borderRadius: ONBOARDING_RADII.lg,
                            backgroundColor: COLORS.primary,
                          }}
                        >
                          <MaterialIcons
                            name={icon}
                            size={ONBOARDING_SPACING.xl}
                            color={COLORS.onPrimary}
                          />
                        </View>
                      ) : null}
                      {title ? (
                        <Text
                          style={{
                            ...ONBOARDING_TYPOGRAPHY.title,
                            color: COLORS.text.primary,
                            flex: isPhone ? undefined : 1,
                            minWidth: 0,
                          }}
                        >
                          {title}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}
                  {subtitle ? (
                    <Text
                      style={{ ...ONBOARDING_TYPOGRAPHY.body, color: COLORS.text.secondary }}
                    >
                      {subtitle}
                    </Text>
                  ) : null}
                </View>
              ) : null}

              {children}

              <View style={{ marginTop: 'auto' }}>
                <Button
                  variant="default"
                  size="default"
                  onPress={() => {
                    if (primaryDisabled) return;
                    primaryCta.onPress();
                  }}
                  disabled={primaryDisabled}
                  accessibilityLabel={primaryCta.label}
                  accessibilityState={{
                    disabled: primaryDisabled,
                    busy: Boolean(primaryCta.loading),
                  }}
                  style={({ pressed }) => ({
                    minHeight: ONBOARDING_CONTROL_HEIGHT,
                    borderRadius: ONBOARDING_RADII.md,
                    backgroundColor: primaryDisabled
                      ? COLORS.primaryDisabled
                      : pressed
                        ? COLORS.primaryPressed
                        : COLORS.primary,
                  })}
                >
                  {primaryCta.loading ? (
                    <View className="flex-row items-center">
                      <ActivityIndicator size="small" color={COLORS.onPrimary} />
                      <Text
                        style={{
                          ...ONBOARDING_TYPOGRAPHY.button,
                          color: COLORS.onPrimary,
                          marginLeft: ONBOARDING_SPACING.sm,
                        }}
                      >
                        {primaryCta.label}
                      </Text>
                    </View>
                  ) : (
                    <Text style={{ ...ONBOARDING_TYPOGRAPHY.button, color: COLORS.onPrimary }}>
                      {primaryCta.label}
                    </Text>
                  )}
                </Button>
                {secondaryCta ? (
                  <TouchableOpacity
                    onPress={secondaryCta.onPress}
                    disabled={secondaryCta.disabled}
                    className="items-center justify-center"
                    accessibilityRole="button"
                    accessibilityLabel={secondaryCta.label}
                    accessibilityState={{ disabled: Boolean(secondaryCta.disabled) }}
                    style={{
                      minHeight: ONBOARDING_CONTROL_HEIGHT,
                      marginTop: ONBOARDING_SPACING.xs,
                    }}
                  >
                    <Text
                      style={{
                        ...ONBOARDING_TYPOGRAPHY.bodySmall,
                        color: secondaryCta.disabled ? COLORS.text.disabled : COLORS.text.muted,
                      }}
                    >
                      {secondaryCta.label}
                    </Text>
                  </TouchableOpacity>
                ) : null}
                {helperText ? (
                  <View
                    className="flex-row items-start"
                    style={{
                      borderRadius: ONBOARDING_RADII.sm,
                      padding: ONBOARDING_SPACING.md,
                      marginTop: ONBOARDING_SPACING.sm,
                      backgroundColor: COLORS.primarySubtle,
                    }}
                  >
                    <MaterialIcons
                      name="info-outline"
                      size={ONBOARDING_SPACING.lg}
                      color={COLORS.primary}
                      style={{ marginTop: ONBOARDING_SPACING.xs / 2, marginRight: ONBOARDING_SPACING.sm }}
                    />
                    <Text
                      style={{
                        ...ONBOARDING_TYPOGRAPHY.caption,
                        color: COLORS.text.secondary,
                        flex: 1,
                      }}
                    >
                      {helperText}
                    </Text>
                  </View>
                ) : null}
              </View>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
